import React, { useState } from 'react';
import { Calendar, Clock, FileText, Book, ArrowLeft, Send, Eye } from 'lucide-react';
import type { BetaAssignment, BetaFeedback } from '../../../types';
import { supabase } from '../../../lib/supabase';

interface AssignmentViewProps {
  assignment: BetaAssignment;
  feedback: BetaFeedback[];
  isAuthor: boolean;
  onBack: () => void;
  onUpdateStatus: (status: 'in_progress' | 'completed') => Promise<void>;
  onSubmitFeedback: (content: string) => Promise<void>;
}

export default function AssignmentView({
  assignment,
  feedback,
  isAuthor,
  onBack,
  onUpdateStatus,
  onSubmitFeedback
}: AssignmentViewProps) {
  const [newFeedback, setNewFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chapterContent, setChapterContent] = useState<string | null>(null);
  const [loadingChapter, setLoadingChapter] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFeedback.trim()) return;

    try {
      setSubmitting(true);
      setError(null);
      await onSubmitFeedback(newFeedback.trim());
      setNewFeedback('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewChapter = async () => {
    if (!assignment.chapter_id) return;

    try {
      setLoadingChapter(true);
      setError(null);

      // Get the chapter version for this assignment
      const { data: versionData, error: versionError } = await supabase
        .from('chapter_versions')
        .select('content')
        .eq('assignment_id', assignment.id)
        .single();

      if (versionError) throw versionError;
      if (!versionData) throw new Error('No chapter version found');

      setChapterContent(versionData.content);
    } catch (err) {
      console.error('Error loading chapter:', err);
      setError(err instanceof Error ? err.message : 'Failed to load chapter');
      setChapterContent(null);
    } finally {
      setLoadingChapter(false);
    }
  };

  const handleDownload = async () => {
    if (!assignment.document_url) return;

    try {
      // Get the original file extension from the URL
      const extension = assignment.document_url.split('.').pop() || '';
      
      // Create a sanitized filename
      const sanitizedTitle = assignment.title
        .replace(/[^a-z0-9]/gi, '_')
        .toLowerCase();
      
      // Create the full filename with extension
      const filename = `${sanitizedTitle}.${extension}`;

      // Fetch the file
      const response = await fetch(assignment.document_url);
      const blob = await response.blob();

      // Create a download link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error('Error downloading file:', err);
      setError('Failed to download file');
    }
  };

  return (
    <div className="space-y-6">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Assignments
      </button>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{assignment.title}</h2>
            {assignment.description && (
              <p className="text-gray-600 mt-2">{assignment.description}</p>
            )}
          </div>

          {!isAuthor && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => onUpdateStatus('in_progress')}
                disabled={assignment.status === 'in_progress'}
                className="px-4 py-2 bg-yellow-100 text-yellow-800 rounded-lg hover:bg-yellow-200 disabled:opacity-50"
              >
                Start Reading
              </button>
              <button
                onClick={() => onUpdateStatus('completed')}
                disabled={assignment.status === 'completed'}
                className="px-4 py-2 bg-green-100 text-green-800 rounded-lg hover:bg-green-200 disabled:opacity-50"
              >
                Mark Complete
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-4 text-sm text-gray-500 mb-6">
          {assignment.chapter_id ? (
            <div className="flex items-center gap-1">
              <Book className="w-4 h-4" />
              <span>Chapter Assignment</span>
            </div>
          ) : assignment.document_url ? (
            <div className="flex items-center gap-1">
              <FileText className="w-4 h-4" />
              <span>Document Review</span>
            </div>
          ) : null}
          
          {assignment.due_date && (
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              <span>Due {new Date(assignment.due_date).toLocaleDateString()}</span>
            </div>
          )}
          
          <div className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            <span>Created {new Date(assignment.created_at).toLocaleDateString()}</span>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {assignment.chapter_id && (
          <div className="mb-6">
            <button
              onClick={handleViewChapter}
              disabled={loadingChapter}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200"
            >
              <Eye className="w-4 h-4" />
              {loadingChapter ? 'Loading...' : 'View Chapter'}
            </button>

            {chapterContent && (
              <div className="mt-4 p-6 bg-gray-50 rounded-lg prose max-w-none">
                <div dangerouslySetInnerHTML={{ __html: chapterContent }} />
              </div>
            )}
          </div>
        )}

        {assignment.document_url && (
          <div className="mb-6">
            <button
              onClick={handleDownload}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200"
            >
              <FileText className="w-4 h-4" />
              Download Document
            </button>
          </div>
        )}

        <div className="space-y-6">
          <h3 className="text-lg font-semibold text-gray-900">Feedback</h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <textarea
              value={newFeedback}
              onChange={(e) => setNewFeedback(e.target.value)}
              placeholder="Share your thoughts and feedback..."
              className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              rows={4}
            />
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={submitting || !newFeedback.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
                Submit Feedback
              </button>
            </div>
          </form>

          <div className="space-y-4">
            {feedback.map((item) => (
              <div key={item.id} className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-medium text-gray-900">
                    {item.profiles.full_name || item.profiles.username}
                  </div>
                  <div className="text-sm text-gray-500">
                    {new Date(item.created_at).toLocaleString()}
                  </div>
                </div>
                <div className="text-gray-700 whitespace-pre-wrap">
                  {item.content}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}